import { PersonaIcon } from '@persona-id/design-tokens/icons';
import { ChangeEvent, useCallback, VFC } from 'react';
import styled from 'styled-components';

import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import StepConfigTypeCard from 'dashboard/components/WorkflowControlPanel/StepConfigTypeCard';
import TriggerAdvancedConfigurationPanel from 'dashboard/components/WorkflowControlPanel/TriggerAdvancedConfigurationPanel/TriggerAdvancedConfigurationPanel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { TransactionType } from 'dashboard/models/transaction-type';
import { SupportedComputedTriggerEvent, TriggerComputedField } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { headingMixins } from 'design-system/mixins/text';
import { EventName } from 'lib/constants/events';

const SubTitle = styled.div`
  ${headingMixins.sm}
`;

const SUPPORTED_EVENTS: SupportedComputedTriggerEvent[] = [EventName.TransactionCreated];
const CONFIG_OPTIONS = SUPPORTED_EVENTS.map((eventName) => ({
  label: eventName,
  value: eventName,
}));

interface TriggerConfigComputedFieldProps {
  trigger: TriggerComputedField;
  editable: boolean;
  errors: WorkflowError[];
  transactionTypes: TransactionType[];
  updateTrigger: (newTrigger: TriggerComputedField) => void;
  handleClose: () => void;
  workflowVersionExpiration: number;
  updateWorkflowVersionExpiration: (expiration: number) => void;
}

const TriggerConfigComputedField: VFC<TriggerConfigComputedFieldProps> = ({
  trigger,
  editable,
  errors,
  transactionTypes = [],
  updateTrigger,
  handleClose,
  workflowVersionExpiration,
  updateWorkflowVersionExpiration,
}) => {
  const { renderErrorMessageFor } =
    useWorkflowError<keyof TriggerComputedField['attributes']>(errors);
  const onConfigChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      updateTrigger({
        ...trigger,
        attributes: {
          ...trigger.attributes,
          config: e.target.checked
            ? [...trigger.attributes.config, e.target.value as SupportedComputedTriggerEvent]
            : [...trigger.attributes.config].filter(
                (v) => v !== (e.target.value as SupportedComputedTriggerEvent),
              ),
        },
      });
    },
    [updateTrigger, trigger],
  );

  const schemaType = transactionTypes
    .find(
      (transactionType) => transactionType.id === trigger.attributes['field-schema-object-token'],
    )
    ?.attributes?.[
      'field-schemas'
    ]?.find((schema) => schema.key === trigger.attributes['field-key'])?.type;

  return (
    <>
      <Spacer height={2} />
      <StepConfigTypeCard
        icon={<PersonaIcon size={24} />}
        title='Computed Field'
        description='Trigger to process computed field'
      />
      <Spacer height={2} />
      <SubTitle>Configuration</SubTitle>
      <ParamLabel labelText='Field Key' htmlFor='trigger-config-computed-field__field-key' />
      <Input
        id='trigger-config-computed-field__field-key'
        value={trigger.attributes['field-key']}
        // Input is intentionally unconfigurable and is configured by the transaction type field
        disabled
      />
      {renderErrorMessageFor('field-key')}
      {schemaType && (
        <>
          <Spacer height={2} />
          <ParamLabel
            labelText='Field Schema'
            htmlFor='trigger-config-computed-field__schema-type'
          />
          {/* Input is intentionally unconfigurable and is configured by the transaction type field */}
          <Input id='trigger-config-computed-field__schema-type' value={schemaType} disabled />
        </>
      )}
      <Spacer height={2} />
      <ParamLabel labelText='Event Type' />
      {CONFIG_OPTIONS.map((option) => (
        <Checkbox
          key={`trigger-config-computed-field__${option.value}-checkbox`}
          disabled={!editable}
          checked={trigger.attributes.config.includes(option.value)}
          value={option.value}
          label={option.label}
          onChange={onConfigChange}
        />
      ))}
      {renderErrorMessageFor('config')}
      <Spacer height={2} />
      <TriggerAdvancedConfigurationPanel
        editable={editable}
        workflowVersionExpiration={workflowVersionExpiration}
        updateWorkflowVersionExpiration={updateWorkflowVersionExpiration}
      />
      <Spacer height={3} />
      <HStack equal={true}>
        <Button
          id='trigger-config-computed-field__close-button'
          aria-label='Close panel'
          onClick={handleClose}
        >
          Close
        </Button>
      </HStack>
    </>
  );
};

export default TriggerConfigComputedField;
